import { useMemo } from 'react'

import { Heading, SkeletonBox } from '@circlefin/components'
import { useMoney } from '@circlefin/hooks'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import { Content } from '@shared/components/layout'
import { TradeTransactionType, useTradeByJobIdQuery } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { TradeCard } from '../../components'

export interface ConvertDetailProps {
  /**
   * Convert Job ID.
   */
  jobId: string
  /**
   * Convert transaction type.
   */
  type: TradeTransactionType
}

export const ConvertDetail: React.FC<ConvertDetailProps> = ({
  jobId,
  type,
}) => {
  const { t } = useTranslation('payments')

  const { money } = useMoney()

  const { data, loading, error } = useTradeByJobIdQuery({
    variables: { jobId },
  })

  const amount = useMemo(() => {
    // If type is convert_mint, we want to show the to amount in the title
    if (type === TradeTransactionType.convert_mint) {
      const to = data?.tradeByJobId.to
      if (to?.amount == null || to.currency == null) return

      return money({ number: to.amount, variant: to.currency })
    }

    // If type is convert_withdrawal, we want to show the from amount in the title
    const from = data?.tradeByJobId.from
    if (from?.amount == null || from.currency == null) return

    return money({ number: from.amount, variant: from.currency })
  }, [data?.tradeByJobId.from, data?.tradeByJobId.to, money, type])

  return (
    <Content>
      <GraphQLErrorBoundary error={error}>
        <Heading variant="page/sm">
          <Heading.Eyebrow>
            <SkeletonBox className="my-1 h-4 w-32" loading={loading}>
              {t(`convertDetail.eyebrow.${type}`, {
                currency:
                  type === TradeTransactionType.convert_mint
                    ? data?.tradeByJobId.from.currency
                    : data?.tradeByJobId.to.currency,
              })}
            </SkeletonBox>
          </Heading.Eyebrow>
          <Heading.Header>
            <SkeletonBox className="my-2 h-8 w-48" loading={loading}>
              {t(`convertDetail.title.${type}`, {
                amount,
              })}
            </SkeletonBox>
          </Heading.Header>
          <Heading.Intro>
            <SkeletonBox className="my-1 h-4 w-58" loading={loading}>
              {t('convertDetail.id', { id: data?.tradeByJobId.id })}
            </SkeletonBox>
          </Heading.Intro>
        </Heading>
        <TradeCard
          loading={loading}
          trade={data?.tradeByJobId}
          tradeType={type}
        />
      </GraphQLErrorBoundary>
    </Content>
  )
}
